export const CHECKOUT_URL = "https://feeding-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/rNwGocT89VaH7yxY4nWF9Y/MOCKUP_FEEDING-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/9FJuwFhKVH5QcGVrSPh2ta/DR._ELOWEN_VARELLI_-_SENSORY.png",
};

export const struggles = [
  "Moving between multiple assessment notes without a unified structure.",
  "Spending additional time organizing caregiver reports and clinical observations.",
  "Struggling to distinguish documented findings from clinical hypotheses.",
  "Rewriting similar evaluation and planning documents.",
  "Developing therapy goals without a clearly documented rationale.",
  "Finding it difficult to communicate priorities across an interdisciplinary team.",
  "Searching for the right documentation format every time a new case arrives.",
];

export const caseFactors = [
  "Caregiver observations",
  "Feeding history",
  "Sensory considerations",
  "Oral-motor concerns",
  "Behavioral patterns",
  "Environmental factors",
  "Potential medical issues",
];

export const beforeItems = [
  "Caregiver information scattered across multiple notes.",
  "Feeding observations documented without a consistent structure.",
  "Clinical concerns mixed with assumptions that still require verification.",
  "Referral considerations addressed inconsistently.",
  "Therapy goals written without a reusable planning framework.",
  "Follow-up information difficult to compare across sessions.",
  "Interdisciplinary communication requiring additional documentation work.",
];

export const afterItems = [
  "Organize case information using a repeatable intake framework.",
  "Document feeding observations using structured clinical worksheets.",
  "Categorize relevant factors without jumping to unsupported conclusions.",
  "Flag concerns that require further assessment or referral.",
  "Build individualized therapy planning drafts with measurable goals.",
  "Track documented changes using consistent follow-up templates.",
  "Prepare concise summaries for caregiver and interdisciplinary communication.",
];

export const stages = [
  {
    key: "organize",
    name: "Organize",
    title: "Build a Structured Case Profile",
    text: "Collect and categorize referral information, caregiver concerns, feeding history, current routines, and relevant background information.",
    output: "Organized Pediatric Feeding Case Profile",
  },
  {
    key: "observe",
    name: "Observe",
    title: "Document Relevant Feeding Behaviors and Functional Patterns",
    text: "Use structured observation tools to record mealtime participation, feeding-related behaviors, environmental considerations, and other findings within your professional scope.",
    output: "Feeding Observation Record",
  },
  {
    key: "prioritize",
    name: "Prioritize",
    title: "Identify Clinical Questions and Referral Considerations",
    text: "Separate observed findings, caregiver-reported information, clinical hypotheses, and concerns requiring further assessment.",
    output: "Clinical Priorities & Referral Considerations Matrix",
  },
  {
    key: "plan",
    name: "Plan",
    title: "Translate Assessment Information Into Individualized Goals",
    text: "Organize documented priorities into functional, measurable therapy goals and appropriate planning considerations.",
    output: "Individualized Feeding Therapy Planning Draft",
  },
  {
    key: "track",
    name: "Track",
    title: "Create a Consistent Follow-Up Structure",
    text: "Document session observations, goal-related progress, caregiver feedback, and factors that may require reassessment.",
    output: "Feeding Therapy Progress Tracker",
  },
  {
    key: "coordinate",
    name: "Coordinate",
    title: "Prepare Clear Professional Communication",
    text: "Summarize relevant findings, documented priorities, referral needs, and follow-up considerations for appropriate interdisciplinary coordination.",
    output: "Interdisciplinary Communication Summary",
  },
];

export const volumes = [
  {
    number: 1,
    title: "Pediatric Feeding Case Intake & Organization Blueprint™",
    tagline: "Build a Clear Starting Point for Every Feeding Case",
    intro:
      "The first challenge in pediatric feeding assessment is organizing the information available before drawing conclusions. This volume provides a structured framework for collecting, categorizing, and documenting case information.",
    items: [
      "Pediatric Feeding Intake Checklist",
      "Caregiver Interview Organization Template",
      "Feeding History Documentation Worksheet",
      "Mealtime Routine Mapping Tool",
      "Current Feeding Concerns Summary",
      "Initial Clinical Questions Checklist",
    ],
    outcome:
      "A structured case profile that consolidates relevant background information, caregiver reports, and initial assessment questions.",
    deliverable: "Pediatric Feeding Case Profile Template",
    note: "",
  },
  {
    number: 2,
    title: "Pediatric Feeding Observation & Documentation Blueprint™",
    tagline: "Turn Feeding Observations Into Organized Clinical Records",
    intro:
      "Feeding-related observations can involve multiple interacting factors. This volume helps professionals document relevant findings systematically while distinguishing observations from interpretations.",
    items: [
      "Structured Mealtime Observation Worksheet",
      "Food Acceptance & Participation Record",
      "Texture-Related Observation Matrix",
      "Feeding Environment Documentation Checklist",
      "Sensory & Motor Considerations Recording Tool",
      "Observation-to-Clinical-Question Mapping Sheet",
    ],
    outcome:
      "A consistent documentation structure for recording relevant feeding observations and identifying questions that may require further evaluation.",
    deliverable: "Pediatric Feeding Observation Record",
    note: "",
  },
  {
    number: 3,
    title: "Feeding Clinical Priorities & Referral Decision Map™",
    tagline: "Organize Relevant Factors Without Overlooking Referral Considerations",
    intro:
      "Not every feeding concern belongs within the same professional scope or requires the same next step. This volume provides a structured way to organize findings, identify unanswered clinical questions, and document referral considerations.",
    items: [
      "Feeding Concern Categorization Matrix",
      "Clinical Findings vs. Hypotheses Worksheet",
      "Medical & Swallowing Concern Escalation Checklist",
      "Scope-of-Practice Decision Support Sheet",
      "Interdisciplinary Referral Considerations Map",
      "Clinical Priority Documentation Template",
    ],
    outcome:
      "A documented overview of relevant findings, unresolved questions, professional scope considerations, and appropriate next-step discussions.",
    deliverable: "Clinical Priorities & Referral Considerations Matrix",
    note: "Important: These tools do not diagnose dysphagia, determine swallowing safety, establish medical clearance, or replace urgent assessment and referral when indicated.",
  },
  {
    number: 4,
    title: "Individualized Pediatric Feeding Therapy Planning Blueprint™",
    tagline: "Build Therapy Planning Drafts Around Documented Functional Priorities",
    intro:
      "Once relevant assessment information has been organized, the next challenge is translating findings into an individualized planning structure. This volume provides templates for connecting documented priorities with functional goals and professional intervention considerations.",
    items: [
      "Functional Goal Development Worksheet",
      "Individualized Planning Framework",
      "Goal Prioritization Matrix",
      "Caregiver Participation Planning Template",
      "Therapy Session Structure Worksheet",
      "Goal-to-Observation Alignment Checklist",
    ],
    outcome:
      "A structured therapy planning draft containing individualized goals, documented rationale, participation considerations, and review criteria.",
    deliverable: "Individualized Pediatric Feeding Therapy Plan Template",
    note: "All intervention decisions must remain within the treating professional’s competence and the child’s individualized clinical needs.",
  },
  {
    number: 5,
    title: "Pediatric Feeding Progress Tracking & Reassessment Blueprint™",
    tagline: "Make Follow-Up Documentation Easier to Organize and Review",
    intro:
      "A useful therapy plan requires a consistent way to document what happens after the initial assessment. This volume focuses on organizing follow-up observations and reviewing documented goals over time.",
    items: [
      "Session Progress Documentation Sheet",
      "Functional Goal Tracking Matrix",
      "Caregiver Feedback Record",
      "Mealtime Participation Monitoring Worksheet",
      "Reassessment Considerations Checklist",
      "Therapy Plan Review Template",
    ],
    outcome:
      "A repeatable follow-up documentation system that helps you compare observations, review goals, and identify when the plan may need reconsideration.",
    deliverable: "Pediatric Feeding Progress Tracking System",
    note: "",
  },
  {
    number: 6,
    title: "Pediatric Feeding Interdisciplinary Coordination Blueprint™",
    tagline: "Organize Clear Communication Across the Child’s Care Team",
    intro:
      "Pediatric feeding cases may involve multiple professionals, caregivers, and settings. This volume helps organize communication without duplicating entire assessments or losing important contextual information.",
    items: [
      "Interdisciplinary Case Summary Template",
      "Referral Communication Worksheet",
      "Caregiver Education Planning Checklist",
      "Team Meeting Preparation Sheet",
      "Shared Goal Documentation Matrix",
      "Follow-Up Coordination Checklist",
    ],
    outcome:
      "A concise communication framework for documenting relevant findings, coordinating next steps, and supporting continuity across appropriate members of the care team.",
    deliverable: "Pediatric Feeding Interdisciplinary Coordination Pack",
    note: "",
  },
];

export const bonuses = [
  {
    number: 1,
    title: "Pediatric Feeding Initial Assessment Checklist™",
    tagline: "A Ready-to-Use Assessment Organization Checklist",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/5c8ad47e-58c3-4e72-ad22-417b8ae4c48d.jpg",
    text: "A structured checklist for reviewing case information, caregiver concerns, feeding history, observations, and outstanding clinical questions.",
    items: [
      "Initial Information Review Checklist",
      "Assessment Preparation Worksheet",
      "Missing Information Tracker",
      "Assessment Completion Review Sheet",
    ],
    why: "Helps you identify missing documentation and organize the assessment process before preparing your clinical summary.",
    note: "",
  },
  {
    number: 2,
    title: "Pediatric Feeding Red Flag & Referral Checklist™",
    tagline: "A Practical Tool for Organizing Safety and Referral Considerations",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/132a84c9-5f87-4f29-83c4-4f8362d765ef.jpg",
    text: "A documentation aid for recording concerns that may require prompt medical evaluation, swallowing assessment, or interdisciplinary input.",
    items: [
      "Concern Recognition Checklist",
      "Referral Considerations Worksheet",
      "Escalation Documentation Template",
      "Professional Scope Review Sheet",
    ],
    why: "Provides a consistent structure for documenting concerns and communicating the need for further evaluation.",
    note: "Not a validated screening instrument or a substitute for emergency protocols, medical evaluation, or specialist assessment.",
  },
  {
    number: 3,
    title: "Pediatric Feeding SMART Goal Builder™",
    tagline: "Turn Documented Functional Priorities Into Measurable Planning Drafts",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/55d75fe6-bc27-4be0-bc6c-c787b40cbe16.jpg",
    text: "A goal-writing toolkit designed to support individualized therapy documentation.",
    items: [
      "SMART Goal Construction Worksheet",
      "Functional Participation Goal Templates",
      "Baseline Documentation Form",
      "Goal Review Criteria Checklist",
    ],
    why: "Helps organize goals around observable participation measures, individualized baselines, and review criteria.",
    note: "",
  },
  {
    number: 4,
    title: "Pediatric Feeding Caregiver Interview Toolkit™",
    tagline: "Capture Relevant Mealtime Information With Greater Consistency",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/152c0740-44e5-436d-9951-590516c882eb.jpg",
    text: "A reusable set of caregiver interview and documentation tools.",
    items: [
      "Caregiver Interview Question Bank",
      "Mealtime Routine Interview Sheet",
      "Caregiver Priorities Worksheet",
      "Home Context Documentation Template",
    ],
    why: "Helps organize caregiver-reported information while preserving the distinction between reported concerns and directly observed findings.",
    note: "",
  },
  {
    number: 5,
    title: "Pediatric Feeding Therapy Session Planner™",
    tagline: "Organize Session Objectives, Observations, and Follow-Up Notes",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/8c7e5e98-c944-4ba5-80de-ead5947afc07.jpg",
    text: "A practical planning resource for documenting individual therapy sessions.",
    items: [
      "Session Planning Template",
      "Goal-to-Activity Alignment Sheet",
      "Session Observation Record",
      "Next-Session Preparation Checklist",
    ],
    why: "Provides a consistent documentation structure that can be adapted to the child’s individualized care plan.",
    note: "",
  },
];

export const caseSteps = [
  {
    title: "Start With the Case Profile",
    text: "Use Volume 1 to document referral information, caregiver concerns, relevant history, and initial clinical questions.",
    result: "An organized starting record.",
  },
  {
    title: "Structure Your Observations",
    text: "Use Volume 2 to record relevant feeding-related observations within your professional scope.",
    result: "A structured observation document.",
  },
  {
    title: "Review Clinical Priorities",
    text: "Use Volume 3 to distinguish documented findings from unresolved questions and identify whether additional evaluation or referral is indicated.",
    result: "A clinical priorities and referral considerations record.",
  },
  {
    title: "Draft Individualized Goals",
    text: "When appropriate assessment information is available, use Volume 4 to organize functional priorities and create a therapy planning draft.",
    result: "A documented individualized plan for professional review.",
  },
  {
    title: "Prepare Follow-Up Documentation",
    text: "Use Volume 5 to establish a consistent way to record subsequent observations and review goals.",
    result: "A reusable progress tracking structure.",
  },
  {
    title: "Coordinate Next Steps",
    text: "Use Volume 6 to prepare relevant summaries for caregivers and other professionals involved in the child’s care.",
    result: "A structured interdisciplinary communication document.",
  },
];

export const audiences = [
  {
    title: "Pediatric Occupational Therapists",
    text: "Organize feeding-related participation concerns, caregiver information, relevant observations, and individualized planning considerations within your professional scope.",
  },
  {
    title: "Speech-Language Pathologists With Feeding Expertise",
    text: "Structure feeding-related documentation, organize clinical questions, and support appropriate coordination of feeding and swallowing concerns.",
  },
  {
    title: "Pediatric Feeding Specialists",
    text: "Create consistent assessment records, document priorities, and organize interdisciplinary communication across complex cases.",
  },
  {
    title: "Pediatric Therapy Teams",
    text: "Use shared documentation structures to support clearer communication and more consistent case organization.",
  },
];

export const blueprintIs = [
  "Assessment organization workflows.",
  "Clinical documentation templates.",
  "Decision-support matrices.",
  "Referral consideration checklists.",
  "Individualized planning frameworks.",
  "Progress tracking worksheets.",
  "Interdisciplinary coordination tools.",
];

export const testimonials = [
  {
    name: "Daniela Ríos",
    role: "Pediatric Occupational Therapist · Texas",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/bc823112-f444-4d97-b85f-0a00bed6fc68.jpg",
    quote:
      "I had a 4-year-old with a very restricted repertoire and three pages of caregiver notes that went nowhere. The intake template and the findings vs. hypotheses worksheet turned that mess into a clean case profile in one afternoon. My evaluation summary finally reads like a plan.",
  },
  {
    name: "Karen Whitfield",
    role: "CCC-SLP, Feeding Clinic · Ohio",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/8c73123a-ce49-4e42-bd05-2d2d03e9a00e.jpg",
    quote:
      "The referral considerations map is what sold me. I now document exactly what is observed, what is reported, and what still needs medical input. Our pediatrician told me my referral letters are the clearest she receives.",
  },
  {
    name: "Marcus Bell",
    role: "Pediatric Feeding Specialist · Georgia",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/03461f80-a047-4b2c-bf99-9427f15dc8b3.jpg",
    quote:
      "I stopped rewriting the same goal documents for every new kid. The SMART Goal Builder plus the progress tracker gave me a repeatable structure, and my reassessments take noticeably less time.",
  },
  {
    name: "Aisha Thompson",
    role: "OTR/L, Early Intervention · North Carolina",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/b3057472-2ee5-4df2-b105-d7e4c84cdb63.jpg",
    quote:
      "Caregivers used to leave sessions confused about priorities. With the caregiver interview toolkit and the communication summary, families now get a one-page overview they actually understand. Worth far more than $27.",
  },
  {
    name: "Grace Lin",
    role: "Speech-Language Pathologist · California",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/062cbed3-e44a-458a-a535-cf1e18f58713.jpg",
    quote:
      "Our team of OTs and SLPs now uses the same documentation structure. Team meetings are shorter, and everyone knows which goals belong to whom. It's practical, not another theory manual.",
  },
];

export const includedResources = [
  { label: "Volume 1 — Feeding Case Intake & Organization Blueprint™", access: "Included" },
  { label: "Volume 2 — Feeding Observation & Documentation Blueprint™", access: "Included" },
  { label: "Volume 3 — Clinical Priorities & Referral Decision Map™", access: "Included" },
  { label: "Volume 4 — Individualized Therapy Planning Blueprint™", access: "Included" },
  { label: "Volume 5 — Progress Tracking & Reassessment Blueprint™", access: "Included" },
  { label: "Volume 6 — Interdisciplinary Coordination Blueprint™", access: "Included" },
  { label: "Bonus 1 — Initial Assessment Checklist™", access: "FREE" },
  { label: "Bonus 2 — Red Flag & Referral Checklist™", access: "FREE" },
  { label: "Bonus 3 — SMART Goal Builder™", access: "FREE" },
  { label: "Bonus 4 — Caregiver Interview Toolkit™", access: "FREE" },
  { label: "Bonus 5 — Therapy Session Planner™", access: "FREE" },
];

export const faqs = [
  {
    q: "Is this designed for pediatric occupational therapists?",
    a: "Yes. The collection is designed for pediatric occupational therapists and other qualified pediatric feeding professionals who need structured tools for assessment organization, documentation, and therapy planning.",
  },
  {
    q: "Can speech-language pathologists use these resources?",
    a: "Yes. Speech-language pathologists with appropriate feeding expertise can adapt relevant documentation and planning resources to their professional scope and clinical responsibilities.",
  },
  {
    q: "Is this a pediatric feeding therapy certification?",
    a: "No. This is a digital professional resource collection. It does not provide certification, professional licensure, clinical competency verification, or authorization to perform procedures.",
  },
  {
    q: "Does the Blueprint include ready-to-use templates?",
    a: "Yes. The collection includes structured checklists, worksheets, documentation templates, planning matrices, and decision-support resources.",
  },
  {
    q: "Can I use the tools with different pediatric feeding cases?",
    a: "The resources are designed to be adaptable across relevant pediatric feeding scenarios. Each case still requires individualized professional assessment and appropriate clinical judgment.",
  },
  {
    q: "Does this provide specific treatment protocols?",
    a: "The collection focuses on assessment organization, documentation, planning frameworks, and follow-up tools. It does not replace individualized treatment selection, professional training, or established clinical protocols.",
  },
  {
    q: "Does it help identify when a referral may be necessary?",
    a: "It includes tools for organizing and documenting potential concerns and referral considerations. These are not validated diagnostic or screening instruments and must not replace appropriate clinical evaluation or urgent medical assessment.",
  },
  {
    q: "Can I use the resources in a multidisciplinary setting?",
    a: "Yes. The interdisciplinary coordination materials are designed to support organized professional communication, subject to applicable privacy requirements and clinical responsibilities.",
  },
  { q: "Is this a physical book?", a: "No. Pediatric Feeding Therapy Blueprint™ is a digital product." },
  { q: "How will I receive my purchase?", a: "You’ll receive digital access to the purchased resources after checkout." },
  { q: "Is this a subscription?", a: "No. The $27 purchase is a one-time payment." },
  {
    q: "What if the product doesn’t meet my expectations?",
    a: "Your purchase includes a 7-day money-back guarantee. You can request a refund within the guarantee period.",
  },
];

export const recentBuyers = [
  { name: "Valentina R.", country: "Mexico" },
  { name: "Camila S.", country: "Argentina" },
  { name: "Lucía M.", country: "Spain" },
  { name: "Sofía G.", country: "Colombia" },
  { name: "Mariana P.", country: "Chile" },
  { name: "Andrea L.", country: "Peru" },
  { name: "Gabriela F.", country: "Uruguay" },
  { name: "Fernanda C.", country: "Ecuador" },
  { name: "Daniela V.", country: "Costa Rica" },
  { name: "Paula H.", country: "Guatemala" },
  { name: "Carolina D.", country: "Venezuela" },
  { name: "Isabella N.", country: "Dominican Republic" },
  { name: "Natalia B.", country: "Paraguay" },
  { name: "Ximena T.", country: "Bolivia" },
  { name: "Alejandra Q.", country: "Panama" },
  { name: "Renata O.", country: "Puerto Rico" },
];
